const ACCESS_TOKEN_KEY = "mu_access_token";
const PERMISSIONS_KEY = "mu_permissions";
const SESSION_USER_KEY = "mu_session_user";

/** Strip formatting; keep last 10 digits for Indian mobiles. */
export function normalizePhone(raw) {
  const digits = String(raw || "").replace(/\D/g, "");
  if (digits.length > 10) return digits.slice(-10);
  return digits;
}

export function getAccessToken() {
  try {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  } catch {
    return null;
  }
}

export function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(window.atob(padded));
  } catch {
    return null;
  }
}

/** JWT `exp` as epoch milliseconds, or null if the claim is missing. */
export function getAccessTokenExpiryMs(token = getAccessToken()) {
  const exp = Number(decodeJwtPayload(token)?.exp);
  if (!Number.isFinite(exp) || exp <= 0) return null;
  return exp * 1000;
}

export function isAccessTokenExpired(token = getAccessToken()) {
  if (!token) return true;
  const expMs = getAccessTokenExpiryMs(token);
  if (expMs == null) return false;
  return Date.now() >= expMs;
}

export function hasValidAccessToken() {
  const token = getAccessToken();
  return Boolean(token) && !isAccessTokenExpired(token);
}

/** Login URL, with an expired-session hint when a JWT is present but past `exp`. */
export function loginRedirectPath() {
  const token = getAccessToken();
  if (token && isAccessTokenExpired(token)) return "/login?session=expired";
  return "/login";
}

export function setAccessToken(token) {
  try {
    if (token) localStorage.setItem(ACCESS_TOKEN_KEY, token);
    else localStorage.removeItem(ACCESS_TOKEN_KEY);
  } catch {
    /* ignore */
  }
}

export function clearAuthSession() {
  try {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(PERMISSIONS_KEY);
    localStorage.removeItem(SESSION_USER_KEY);
  } catch {
    /* ignore */
  }
}

export function getStoredPermissions() {
  try {
    const raw = localStorage.getItem(PERMISSIONS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed)
      ? parsed.filter((p) => typeof p === "string").map((p) => p.trim()).filter(Boolean)
      : [];
  } catch {
    return [];
  }
}

export function setStoredPermissions(permissions) {
  const normalized = Array.from(
    new Set(
      (permissions || [])
        .filter((p) => typeof p === "string")
        .map((p) => p.trim())
        .filter(Boolean),
    ),
  );
  try {
    localStorage.setItem(PERMISSIONS_KEY, JSON.stringify(normalized));
  } catch {
    /* ignore */
  }
}

export function getStoredSessionUser() {
  try {
    const raw = localStorage.getItem(SESSION_USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function setStoredSessionUser(user) {
  try {
    if (user) localStorage.setItem(SESSION_USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(SESSION_USER_KEY);
  } catch {
    /* ignore */
  }
}

export function formatApiError(err) {
  const data = err?.response?.data ?? err?.data ?? err;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error?.message === "string" && data.error.message) return data.error.message;
  if (data?.errors && typeof data.errors === "object") {
    return Object.entries(data.errors)
      .flatMap(([k, v]) => (Array.isArray(v) ? v.map((e) => `${k}: ${e}`) : [`${k}: ${v}`]))
      .join(", ");
  }
  const detail = data?.detail ?? data;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg ?? JSON.stringify(d)).join(", ");
  if (typeof err?.message === "string" && err.message) return err.message;
  return "Something went wrong";
}

export function parseAuthResponse(res) {
  const payload = res?.data ?? res ?? {};
  return {
    success: res?.success ?? payload?.success,
    message: res?.message || payload?.message,
    token:
      payload?.access_token ||
      payload?.token ||
      res?.access_token ||
      res?.token,
    permissions: Array.isArray(payload?.rbac)
      ? payload.rbac
      : Array.isArray(payload?.permissions)
        ? payload.permissions
        : Array.isArray(res?.rbac)
          ? res.rbac
          : [],
    qid: payload?.qid ?? res?.qid,
    phone_number: payload?.phone_number ?? res?.phone_number,
    user: payload?.user ?? res?.user,
  };
}
